import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  REGISTRATION_PAYMENT_LABELS,
  isGuestMemberType,
  memberTypeLabel,
  type RegistrationPaymentStatus,
  type TastingDashboardRow,
  type TastingDashboardSlot,
} from '@gwc/shared';
import { dashboardApi } from '../../api/dashboard';
import { toast } from '../../lib/toast';
import { Avatar } from '../Avatar';
import { WineColorChip } from '../WineColorChip';
import { Badge, useConfirm } from '../ui';
import { RegistrationActions } from './RegistrationActions';
import { DASHBOARD_GRID, errorMessage } from './layout';

const PAYMENT_TONE: Record<RegistrationPaymentStatus, Parameters<typeof Badge>[0]['tone']> = {
  not_required: 'neutral',
  unpaid: 'warning',
  paid: 'success',
  waived: 'success',
  refund_due: 'danger',
  refunded: 'neutral',
};

export function DashboardRow({
  eventId,
  row,
  slots,
  ownerNames,
  canAssign,
  selected,
  onSelect,
  onChanged,
}: {
  eventId: string;
  row: TastingDashboardRow;
  slots: TastingDashboardSlot[];
  /** registrationId → person's name, to say whose wine a taken letter holds. */
  ownerNames: Map<string, string>;
  /** Letters can be (re)assigned in any phase before `final`. */
  canAssign: boolean;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onChanged: () => void;
}) {
  const confirm = useConfirm();
  const name = `${row.firstName} ${row.lastName}`;
  const type = memberTypeLabel(row.memberType);

  const review = useMutation({
    mutationFn: (reviewed: boolean) =>
      dashboardApi.setWineReviewed(eventId, row.registrationId, reviewed),
    onSuccess: onChanged,
    onError: (e) => toast.error(errorMessage(e, 'Could not update the proofread mark')),
  });

  const payment = useMutation({
    mutationFn: (status: RegistrationPaymentStatus) =>
      dashboardApi.setPaymentStatus(eventId, row.registrationId, status),
    onSuccess: (_r, status) => {
      toast.success(`${name}: ${REGISTRATION_PAYMENT_LABELS[status].toLowerCase()}`);
      onChanged();
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not update the payment')),
  });

  const [assigning, setAssigning] = useState(false);
  async function changeSlot(wineId: string) {
    const target = slots.find((s) => s.wineId === wineId) ?? null;
    if (target?.registrationId && target.registrationId !== row.registrationId) {
      const owner = ownerNames.get(target.registrationId) ?? target.broughtByName ?? 'someone else';
      const ok = await confirm({
        title: `Letter ${target.letter} is already ${owner}'s wine`,
        description: `Put ${row.firstName}'s wine under ${target.letter} instead? ${owner}'s wine will be left without a letter.`,
        confirmLabel: `Use ${target.letter}`,
      });
      if (!ok) return;
    }
    setAssigning(true);
    try {
      // A registration sits on one slot at most: free the old letter first.
      if (row.slot && row.slot.wineId !== wineId) {
        await dashboardApi.assignSlot(eventId, row.slot.wineId, null);
      }
      if (target) await dashboardApi.assignSlot(eventId, target.wineId, row.registrationId);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not assign the letter'));
    } finally {
      setAssigning(false);
      onChanged();
    }
  }

  async function markPayment(status: RegistrationPaymentStatus, verb: string) {
    const ok = await confirm({ title: `${verb} for ${name}?`, confirmLabel: verb });
    if (ok) payment.mutate(status);
  }

  return (
    <li
      className={`grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-3 md:gap-y-0 md:rounded-none md:border-0 md:border-b md:px-2 md:py-2.5 ${DASHBOARD_GRID} ${
        selected ? 'border-gold-500/50 bg-gold-600/5' : 'border-ink-800 md:border-ink-800/60'
      }`}
    >
      <input
        type="checkbox"
        className="order-1 h-4 w-4 rounded border-ink-800"
        checked={selected}
        onChange={(e) => onSelect(e.target.checked)}
        aria-label={`Tick ${name}`}
      />

      {/* Person */}
      <div className="order-2 flex min-w-0 items-center gap-2.5">
        <Avatar
          firstName={row.firstName}
          lastName={row.lastName}
          avatarUrl={row.avatarUrl}
          size="sm"
        />
        <div className="min-w-0">
          <p className="truncate text-sm text-cream-50">{name}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {row.status === 'waitlist' && <Badge tone="gold">Waitlist</Badge>}
            {isGuestMemberType(row.memberType) && <Badge tone="info">Guest</Badge>}
            {type && !isGuestMemberType(row.memberType) && (
              <span className="truncate text-[11px] text-cream-300/50">{type}</span>
            )}
          </div>
          {row.registeredByName && (
            <p className="truncate text-[11px] text-cream-300/45">
              Added by {row.registeredByName}
            </p>
          )}
        </div>
      </div>

      {/* Letter (top-right on a phone, its own column on a laptop) */}
      <div className="order-3 md:order-6">
        {slots.length === 0 ? (
          <span className="text-xs text-cream-300/40">No letters</span>
        ) : (
          <select
            className="field w-[5.5rem] px-2 py-1.5 text-sm md:w-full"
            aria-label={`Letter for ${name}'s wine`}
            value={row.slot?.wineId ?? ''}
            disabled={!canAssign || assigning || row.status !== 'registered'}
            title={
              row.status !== 'registered'
                ? 'Waitlisted: no letter until they have a seat'
                : undefined
            }
            onChange={(e) => void changeSlot(e.target.value)}
          >
            <option value="">—</option>
            {slots.map((s) => {
              const owner =
                s.registrationId && s.registrationId !== row.registrationId
                  ? (ownerNames.get(s.registrationId) ?? s.broughtByName ?? 'taken')
                  : null;
              return (
                <option key={s.wineId} value={s.wineId}>
                  {owner ? `${s.letter} · ${owner}` : s.letter}
                </option>
              );
            })}
          </select>
        )}
      </div>

      {/* Wine, with the label photo beside it */}
      <div className="order-4 col-span-2 col-start-2 flex min-w-0 items-start gap-3 md:order-4 md:col-span-1 md:col-start-auto">
        {row.labelImageUrl ? (
          <a
            href={row.labelImageUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0"
            title="Open the label photo"
          >
            <img
              src={row.labelImageUrl}
              alt={`Label of ${row.wineName ?? 'the wine'}`}
              loading="lazy"
              className="h-14 w-11 rounded border border-ink-800 object-cover"
            />
          </a>
        ) : (
          <span className="grid h-14 w-11 shrink-0 place-items-center rounded border border-dashed border-ink-800 text-center text-[9px] uppercase leading-tight tracking-wide text-cream-300/40">
            No label
          </span>
        )}
        <div className="min-w-0">
          {row.wineName ? (
            <p className="text-sm text-cream-100">{row.wineName}</p>
          ) : (
            <p className="text-sm italic text-warning">No wine entered</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {row.categoryName && <Badge>{row.categoryName}</Badge>}
            <WineColorChip color={row.wine?.color} />
            {row.wine?.needsReview && <Badge tone="warning">New spelling</Badge>}
            {row.hasWine && !row.wine && (
              <span className="text-[11px] text-cream-300/45">Typed in, not in the Cellar</span>
            )}
          </div>
        </div>
      </div>

      {/* Status controls: one wrapped line on a phone, separate columns on a laptop */}
      <div className="order-5 col-span-2 col-start-2 flex flex-wrap items-center gap-2 md:contents">
        <div className="flex flex-wrap items-center gap-1.5 md:order-3">
          <Badge tone={PAYMENT_TONE[row.paymentStatus]}>
            {REGISTRATION_PAYMENT_LABELS[row.paymentStatus]}
          </Badge>
          {row.paymentStatus === 'unpaid' && (
            <>
              <button
                type="button"
                className="btn-ghost px-2 py-0.5 text-xs"
                disabled={payment.isPending}
                onClick={() => void markPayment('paid', 'Mark paid')}
              >
                Paid
              </button>
              <button
                type="button"
                className="btn-quiet px-2 py-0.5 text-xs"
                disabled={payment.isPending}
                onClick={() => void markPayment('waived', 'Waive payment')}
              >
                Waive
              </button>
            </>
          )}
          {row.paymentStatus === 'refund_due' && (
            <button
              type="button"
              className="btn-ghost px-2 py-0.5 text-xs"
              disabled={payment.isPending}
              onClick={() => void markPayment('refunded', 'Mark refunded')}
            >
              Refunded
            </button>
          )}
        </div>

        <div className="md:order-5">
          <button
            type="button"
            aria-pressed={row.wineReviewed}
            disabled={!row.hasWine || review.isPending}
            onClick={() => review.mutate(!row.wineReviewed)}
            title={
              !row.hasWine
                ? 'Nothing to proofread yet'
                : row.wineReviewed
                  ? `Proofread${row.wineReviewedByName ? ` by ${row.wineReviewedByName}` : ''}. Click to undo.`
                  : 'Spelling and label checked: mark it proofread'
            }
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition disabled:cursor-not-allowed disabled:opacity-40 ${
              row.wineReviewed
                ? 'border-success/50 bg-success/10 text-success'
                : 'border-ink-800 text-cream-300 hover:border-gold-500 hover:text-gold-200'
            }`}
          >
            <span aria-hidden="true">{row.wineReviewed ? '✓' : '○'}</span>
            {row.wineReviewed ? 'Proofread' : 'Proofread?'}
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 md:order-7">
          <RegistrationActions eventId={eventId} row={row} onChanged={onChanged} />
        </div>
      </div>
    </li>
  );
}
